import React, { useEffect, useMemo, useState, memo, Component } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { Tooltip } from '../components/ui/Tooltip';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { mockData } from '../services/mockData';
import { getOfferProductMetadata, offerProductOptions, offerProductTypes, offerScopes } from '../services/offerCatalog';
import { ArrowLeft, Edit, Trash2, ClipboardList, Ticket, Plus, Phone, Mail, Globe, MapPin, Building2, Users, FileText, Receipt, AlertTriangle, Wrench, GraduationCap, Save, X, Check, ChevronRight, User, Smartphone, Shield, Info, Calendar, Eye, Clock, HardHat, TrendingUp, Hammer, AlertCircle, CheckCircle2, Stethoscope, ExternalLink, Briefcase, Lightbulb, Euro, Award, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClientSectionPage } from './ClientSectionPage';
import { ClientStatusBanner } from '../components/ClientStatusBanner';
import { Kanban } from '../components/ui/Kanban';
import { Timeline } from '../components/ui/Timeline';
import { actionsStore, sortActions } from '../services/actionsStore';
import { ActionsTable } from '../components/operations/ActionsTable';
// Mock Data for the Detail Page
const zones = ['Arezzo', 'Casentino', 'Valdarno', 'Valdichiana', 'Valtiberina - Perugia', 'Siena', 'Montalcino', 'Firenze – Prato', 'Grosseto', 'Pisa – Livorno – Lucca – Massa Carrara'];
const summarySections = [{
  id: 'workers',
  title: 'Risorse umane',
  icon: HardHat,
  count: 12
}, {
  id: 'deadlines',
  title: 'Scadenze',
  icon: Calendar,
  count: 3
}, {
  id: 'contracts',
  title: 'Offerte e contratti',
  icon: FileText,
  count: 5
}, {
  id: 'invoices',
  title: 'Fatturazione',
  icon: Receipt,
  count: 8
}, {
  id: 'training',
  title: 'Formazione',
  icon: GraduationCap,
  count: 9
}, {
  id: 'operations',
  title: 'Attività operative',
  icon: ClipboardList,
  count: 4
}, {
  id: 'non_conformities',
  title: 'Non conformità',
  icon: AlertTriangle,
  count: 2
}, {
  id: 'improvements',
  title: 'Miglioramenti',
  icon: TrendingUp,
  count: 3
}, {
  id: 'equipment',
  title: 'Asset e manutenzioni',
  icon: Wrench,
  count: 15
}, {
  id: 'maintenance',
  title: 'Manutenzione',
  icon: Hammer,
  count: 6
}];
// Helper to generate realistic mock data based on section
const getSectionData = (sectionId: string) => {
  switch (sectionId) {
    case 'workers':
      return [{
        id: 1,
        col1: 'Mario Rossi',
        col2: 'Operaio',
        col3: 'Attivo',
        col4: '12/05/2024'
      }, {
        id: 2,
        col1: 'Luigi Verdi',
        col2: 'Impiegato',
        col3: 'Attivo',
        col4: '10/01/2024'
      }, {
        id: 3,
        col1: 'Anna Bianchi',
        col2: 'Tecnico',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }];
    case 'deadlines':
      return [{
        id: 1,
        col1: 'Rinnovo DVR',
        col2: 'Sicurezza',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }, {
        id: 2,
        col1: 'Analisi acque',
        col2: 'Ambiente',
        col3: 'Programmata',
        col4: '20/06/2024'
      }, {
        id: 3,
        col1: 'Verifica estintori',
        col2: 'Antincendio',
        col3: 'Programmata',
        col4: '30/06/2024'
      }];
    case 'contracts':
      return [{
        id: 1,
        col1: 'CTR-2024-001',
        col2: 'Sicurezza Annuale',
        col3: 'Attivo',
        col4: '01/01/2024'
      }, {
        id: 2,
        col1: 'CTR-2024-005',
        col2: 'HACCP Base',
        col3: 'In scadenza',
        col4: '15/02/2024'
      }, {
        id: 3,
        col1: 'PREV-2024-012',
        col2: 'Formazione',
        col3: 'Bozza',
        col4: '10/05/2024'
      }];
    case 'invoices':
      return [{
        id: 1,
        col1: 'Fatt. 45/2024',
        col2: 'Canone annuale',
        col3: 'Pagata',
        col4: '15/04/2024'
      }, {
        id: 2,
        col1: 'Fatt. 52/2024',
        col2: 'Corso formazione',
        col3: 'In attesa',
        col4: '10/05/2024'
      }, {
        id: 3,
        col1: 'Proforma 12',
        col2: 'Consulenza',
        col3: 'Bozza',
        col4: '20/05/2024'
      }];
    case 'operations':
      return [{
        id: 1,
        col1: 'Sopralluogo',
        col2: 'Sicurezza',
        col3: 'Completato',
        col4: '10/05/2024'
      }, {
        id: 2,
        col1: 'Verifica',
        col2: 'Asset',
        col3: 'Programmato',
        col4: '25/05/2024'
      }, {
        id: 3,
        col1: 'Audit',
        col2: 'Qualità',
        col3: 'In corso',
        col4: '15/05/2024'
      }];
    case 'non_conformities':
      return [{
        id: 1,
        col1: 'NC-2024-01',
        col2: 'Mancanza DPI',
        col3: 'Aperta',
        col4: '12/04/2024'
      }, {
        id: 2,
        col1: 'NC-2024-03',
        col2: 'Documentazione',
        col3: 'Risolta',
        col4: '05/05/2024'
      }, {
        id: 3,
        col1: 'NC-2024-04',
        col2: 'Segnaletica',
        col3: 'Aperta',
        col4: '15/05/2024'
      }];
    case 'improvements':
      return [{
        id: 1,
        col1: 'IMP-001',
        col2: 'Digitalizzazione',
        col3: 'In valutazione',
        col4: '01/03/2024'
      }, {
        id: 2,
        col1: 'IMP-002',
        col2: 'Formazione extra',
        col3: 'Approvato',
        col4: '15/04/2024'
      }, {
        id: 3,
        col1: 'IMP-003',
        col2: 'Nuovi DPI',
        col3: 'Completato',
        col4: '10/01/2024'
      }];
    case 'equipment':
      return [{
        id: 1,
        col1: 'Carrello elevatore',
        col2: 'Logistica',
        col3: 'Conforme',
        col4: '12/02/2024'
      }, {
        id: 2,
        col1: 'Pressa idraulica',
        col2: 'Produzione',
        col3: 'Manutenzione',
        col4: '10/05/2024'
      }, {
        id: 3,
        col1: 'Trapano a colonna',
        col2: 'Officina',
        col3: 'Conforme',
        col4: '15/03/2024'
      }];
    case 'maintenance':
      return [{
        id: 1,
        col1: 'Ordinaria',
        col2: 'Carrello A',
        col3: 'Eseguita',
        col4: '01/04/2024'
      }, {
        id: 2,
        col1: 'Straordinaria',
        col2: 'Pressa B',
        col3: 'In corso',
        col4: '15/05/2024'
      }, {
        id: 3,
        col1: 'Periodica',
        col2: 'Impianto elettrico',
        col3: 'Programmata',
        col4: '20/06/2024'
      }];
    case 'training':
      return [{
        id: 1,
        col1: 'Sicurezza Gen.',
        col2: 'Base',
        col3: 'Valido',
        col4: '12/05/2024'
      }, {
        id: 2,
        col1: 'Primo Soccorso',
        col2: 'Emergenza',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }, {
        id: 3,
        col1: 'RLS',
        col2: 'Specifico',
        col3: 'Scaduto',
        col4: '01/01/2024'
      }];
    default:
      return [];
  }
};
// ... (Keep other helpers and mock data same as original)
const getSectionColumns = (sectionId: string) => {
  switch (sectionId) {
    case 'workers':
      return ['Nominativo', 'Mansione', 'Stato', 'Data assunzione'];
    case 'deadlines':
      return ['Titolo', 'Ambito', 'Stato', 'Scadenza'];
    case 'contracts':
      return ['Codice', 'Oggetto', 'Stato', 'Data stipula'];
    case 'invoices':
      return ['Numero', 'Descrizione', 'Stato', 'Data'];
    case 'operations':
      return ['Attività', 'Tipo', 'Stato', 'Data'];
    case 'non_conformities':
      return ['Codice', 'Oggetto', 'Stato', 'Data apertura'];
    case 'improvements':
      return ['Codice', 'Oggetto', 'Stato', 'Data proposta'];
    case 'equipment':
      return ['Asset', 'Reparto', 'Stato', 'Ultima verifica'];
    case 'maintenance':
      return ['Tipo', 'Oggetto', 'Stato', 'Data'];
    case 'training':
      return ['Corso', 'Tipo', 'Stato', 'Data corso'];
    default:
      return ['Riferimento', 'Descrizione', 'Stato', 'Data'];
  }
};
export function ClientDetailPage() {
  const {
    id,
    section
  } = useParams<{
    id: string;
    section?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(section || null);
  const [refresh, setRefresh] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const [showTicketHistory, setShowTicketHistory] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showNewOfferModal, setShowNewOfferModal] = useState(false);
  const [offerSaveSuccess, setOfferSaveSuccess] = useState(false);
  const [offerProduct, setOfferProduct] = useState('');
  const [offerType, setOfferType] = useState('');
  const [offerProductCode, setOfferProductCode] = useState('');
  const [offerScope, setOfferScope] = useState('');
  const isPending = searchParams.get('pending') === 'true';
  const clientActions = useMemo(() => {
    if (!client || isPending) return [];
    return sortActions(actionsStore.getByClient(client.name));
  }, [client, refresh, isPending]);
  const handleActionUpdate = () => {
    setRefresh((r: number) => r + 1);
  };
  const handleOfferProductSelection = (product: string) => {
    const metadata = getOfferProductMetadata(product);
    setOfferProduct(product);
    setOfferType(metadata?.type || '');
    setOfferProductCode(metadata?.code || '');
  };
  const resetOfferMetadata = () => {
    setOfferProduct('');
    setOfferType('');
    setOfferProductCode('');
    setOfferScope('');
  };
  // Sync section state with URL
  useEffect(() => {
    // Check for edit mode param
    if (searchParams.get('edit') === 'true') {
      setIsEditing(true);
    }
    // Simulate fetch
    const foundClient = mockData.getClients().find(c => c.id === Number(id)) || mockData.getClients()[0];
    // Enrich with extra fields required for this view
    const enrichedClient = {
      ...foundClient,
      piva: '01234567890',
      cf: 'ABCDE12345F',
      ateco: '43.21.01',
      address: 'Via Roma 15, 52100 Arezzo (AR)',
      legalAddress: 'Via Roma 15, 52100 Arezzo (AR)',
      operationalAddresses: ['Via Firenze 8, 52100 Arezzo (AR)', 'Via Casentino 3, 52010 Bibbiena (AR)'],
      legalRep: 'Mario Rossi',
      origin: 'Commerciale SEF',
      accountant: 'Studio Bianchi & Associati',
      activity: 'Costruzioni edili e ristrutturazioni',
      employees: 25,
      employeesCensused: 12,
      newsletter: true,
      clientOf: ['SEF Studio', 'SEFORM'],
      // Contacts
      companyPhone: '0575 123456',
      adminEmail: 'amministrazione@azienda.it',
      consultingEmail: 'consulenza@azienda.it',
      pec: 'azienda@pec.it',
      sdi: 'M5UXCR1',
      contactPerson: 'Giulia Verdi',
      contactMobile: '333 1234567',
      safetyReferent: 'Mario Rossi',
      safetyMobile: '335 9876543',
      trainingEmail: 'formazione@azienda.it',
      infoEmail: 'info@azienda.it',
      newsletterEmail: 'news@azienda.it'
    };
    setClient(enrichedClient);
    setFormData(enrichedClient);
    setLoading(false);
  }, [id, searchParams]);
  if (loading) return <Layout data-id="element-2149" data-mp="l6e0g2lshnqb">
        <div className="p-8" data-id="element-2150" data-mp="l6xkppywwo7i">Caricamento...</div>
      </Layout>;
  if (!client) return <Layout data-id="element-2151" data-mp="2wtzn4x75o10l">
        <div className="p-8" data-id="element-2152" data-mp="31qztzabtx7sr">Cliente non trovato</div>
      </Layout>;
  const handleSave = () => {
    setClient(formData);
    setIsEditing(false);
    if (searchParams.get('edit')) {
      navigate(`/clients/${id}`, {
        replace: true
      });
    }
  };
  // ... (Keep other handlers)
  const handleDeleteItem = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setDeleteItem(null);
    }, 1500);
  };
  const handleSaveItem = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setEditItem(null);
    }, 1500);
  };
  const handleView = (item: any) => {
    setViewItem(item);
    // Reset specific states
    setWorkerTab('info');
    setNewCourses([]);
    setNewVisits([]);
  };
  const handleEditItem = (item: any) => {
    setEditItem(item);
    // Reset specific states
    setRecurrenceMode('single');
    setMultipleDates(['']);
  };
  // Listing View Component
  if (activeSection) {
    return <ClientSectionPage sectionId={activeSection} clientName={client.name} onBack={() => setActiveSection(null)} data-id="element-2153" data-mp="3encuu2zuo4c7" />;
  }
  return <Layout data-id="element-2154" data-mp="34q83lor5dioj">
      {/* Breadcrumb */}
      <div className="mb-6" data-id="element-2155" data-mp="34tciph65hytz">
        <button onClick={() => navigate('/clients')} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-2156" data-mp="2zsyanz4juovy">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-2157" data-mp="2qzl5nyt7nwti" />
          <span data-id="element-2158" data-mp="2m0s0qurzpe4y">Anagrafiche clienti</span>
        </button>
      </div>

      {/* Header */}
      <div className="pb-6 border-b border-slate-200 mb-8" data-id="element-2159" data-mp="2h272h0wat4j5">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4" data-id="element-2160" data-mp="2h3ry6tucmtil">
          <div data-id="element-2161" data-mp="2c22hoyocxnn4">
            <div className="flex items-center gap-3 mb-2" data-id="element-2162" data-mp="2c4op8yb9035c">
              <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2163" data-mp="282jxk54d1glc">
                {client.name}
              </h1>
              <StatusBadge status="Attivo" data-id="element-2164" data-mp="niyeinsjj2xt" />
            </div>
            <p className="text-slate-500 font-mono" data-id="element-2165" data-mp="rk1s13n48mvv">
              N° Cliente: CLI-{client.id.toString().padStart(4, '0')}
            </p>
          </div>
          <div className="flex gap-2" data-id="element-2166" data-mp="wi3bhpogeo7v">
            <Button variant="flat" startContent={<Ticket className="h-4 w-4" data-id="element-2168" data-mp="11gmy7savg41a" />} onClick={() => setShowTicketHistory(true)} data-id="element-2167" data-mp="11j965t1oclk1">
              Ticket
            </Button>
            <Button color={isEditing ? 'success' : 'secondary'} startContent={isEditing ? <Save className="h-4 w-4" data-id="element-2170" data-mp="drlkbpj9s0om" /> : <Edit className="h-4 w-4" data-id="element-2171" data-mp="ilpsud86ogrt" />} onClick={() => isEditing ? handleSave() : setIsEditing(true)} data-id="element-2169" data-mp="8v3lt7ej8ykq">
              {isEditing ? 'Salva modifiche' : 'Modifica'}
            </Button>
            <Button variant="flat" color="danger" startContent={<Trash2 className="h-4 w-4" data-id="element-2173" data-mp="3a6xatpv9ws84" />} data-id="element-2172" data-mp="3e8ag9yugetei">
              Elimina
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-id="element-2174" data-mp="3nx0npq89j9kw">
        {/* Left Column */}
        <div className="xl:col-span-2 space-y-8" data-id="element-2175" data-mp="3j0a8140deg00">
          {/* Client Data Card */}
          <Card title="Dati aziendali" className="pb-2" data-id="element-2176" data-mp="2v94wh11gq9vi">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pb-6" data-id="element-2177" data-mp="2vbr3mzkfynd7">
              {/* Cliente di */}
              <div className="col-span-full" data-id="element-2178" data-mp="357uecttczubr">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2179" data-mp="358na6zqs4zf4">
                  Cliente di
                </label>
                {isEditing ? <div className="flex gap-4" data-id="element-2180" data-mp="kgtvcfvasyrf">
                    <label className="flex items-center gap-2" data-id="element-2181" data-mp="kjpta475bnfv">
                      <input type="checkbox" checked={formData.clientOf.includes('SEF Studio')} onChange={e => {
                    const newSet = e.target.checked ? [...formData.clientOf, 'SEF Studio'] : formData.clientOf.filter((x: string) => x !== 'SEF Studio');
                    setFormData({
                      ...formData,
                      clientOf: newSet
                    });
                  }} data-id="element-2182" data-mp="fhqpwubeoqix" />{' '}
                      SEF Studio
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2183" data-mp="27cibdgmdc671">
                      <input type="checkbox" checked={formData.clientOf.includes('SEFORM')} onChange={e => {
                    const newSet = e.target.checked ? [...formData.clientOf, 'SEFORM'] : formData.clientOf.filter((x: string) => x !== 'SEFORM');
                    setFormData({
                      ...formData,
                      clientOf: newSet
                    });
                  }} data-id="element-2184" data-mp="2cc4m9jeq5aco" />{' '}
                      SEFORM
                    </label>
                  </div> : <div className="flex gap-2" data-id="element-2185" data-mp="1iqqo31d8arxj">
                    {client.clientOf.map((c: string) => <Badge key={c} variant="primary" data-id="element-2186" data-mp="1drdyw9yvx8rs">
                        {c}
                      </Badge>)}
                  </div>}
              </div>

              {/* P.IVA & CF */}
              <div data-id="element-2187" data-mp="2ejvz90qfsjv">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2188" data-mp="2aw38c343a67">
                  P.IVA / Codice Fiscale
                </label>
                {isEditing ? <div className="flex gap-2" data-id="element-2189" data-mp="2a0lb3rs8vp3w">
                    <Input value={formData.piva} onChange={e => setFormData({
                  ...formData,
                  piva: e.target.value
                })} placeholder="P.IVA" data-id="element-2190" data-mp="2dzu4gvjdhkyx" />
                    <Input value={formData.cf} onChange={e => setFormData({
                  ...formData,
                  cf: e.target.value
                })} placeholder="CF" data-id="element-2191" data-mp="2soyn2du2xr7k" />
                  </div> : <p className="font-medium text-slate-900" data-id="element-2192" data-mp="17dgktvbq2kla">
                    {client.piva} / {client.cf}
                  </p>}
              </div>

              {/* Ateco */}
              <div data-id="element-2193" data-mp="w5ten5sqq0l9">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2194" data-mp="w25kc4fh1a5h">
                  Codice Ateco
                </label>
                {isEditing ? <Input value={formData.ateco} onChange={e => setFormData({
                ...formData,
                ateco: e.target.value
              })} data-id="element-2195" data-mp="1614szn08l5sh" /> : <p className="font-medium text-slate-900" data-id="element-2196" data-mp="1jvxdz0ywbe6g">{client.ateco}</p>}
              </div>

              {/* Zona */}
              <div data-id="element-2197" data-mp="1tsjwhj3r1t5b">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2198" data-mp="1tqpd4984m93c">
                  Zona
                </label>
                {isEditing ? <Select value={formData.zone} onChange={e => setFormData({
                ...formData,
                zone: e.target.value
              })} options={zones.map(z => ({
                value: z,
                label: z
              }))} data-id="element-2199" data-mp="3vivft9r2rw5e" /> : <p className="font-medium text-slate-900" data-id="element-2200" data-mp="1yyp3a3yrd3ry">{client.zone}</p>}
              </div>

              {/* Attività Aziendale - MOVED HERE (Right column, below Ateco) */}
              <div data-id="element-2201" data-mp="2rmb5b7sc80cp">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2202" data-mp="2rjp2flge690t">
                  Attività Aziendale
                </label>
                {isEditing ? <Input value={formData.activity} onChange={e => setFormData({
                ...formData,
                activity: e.target.value
              })} data-id="element-2203" data-mp="2hpg8ctbpq80j" /> : <p className="font-medium text-slate-900" data-id="element-2204" data-mp="1cogmv452eolf">
                    {client.activity}
                  </p>}
              </div>

              {/* Indirizzo */}
              <div data-id="element-2205" data-mp="yr0evo74emrk">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2206" data-mp="13nimcvtu4z7l">
                  Indirizzo
                </label>
                {isEditing ? <Input value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} data-id="element-2207" data-mp="ox1lbdl44bb8" /> : <p className="font-medium text-slate-900" data-id="element-2208" data-mp="60a02ym0fm4j">{client.address}</p>}
              </div>

              {/* Sede Legale */}
              <div data-id="element-2209" data-mp="ayblhq6wrxj6">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2210" data-mp="b0xt1ptsud1e">
                  Sede Legale
                </label>
                {isEditing ? <Input value={formData.legalAddress} onChange={e => setFormData({
                ...formData,
                legalAddress: e.target.value
              })} data-id="element-2211" data-mp="3ilffkcuyc8o3" /> : <p className="font-medium text-slate-900" data-id="element-2212" data-mp="5d2o78tbig5x">
                    {client.legalAddress}
                  </p>}
              </div>

              {/* Sede Operativa */}
              <div data-id="element-2213" data-mp="f2vxu49wgl7j">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2214" data-mp="f2ch2qvzk2v3">
                  Sedi Operative
                </label>
                {isEditing ? <div className="space-y-2" data-id="element-2215" data-mp="p1bqiboks2j5">
                    {formData.operationalAddresses.map((addr: string, idx: number) => <div key={idx} className="flex gap-2" data-id="element-2216" data-mp="2ijirbly1lr6e" data-mpi={idx}>
                          <Input value={addr} onChange={e => {
                    const newAddrs = [...formData.operationalAddresses];
                    newAddrs[idx] = e.target.value;
                    setFormData({
                      ...formData,
                      operationalAddresses: newAddrs
                    });
                  }} data-id="element-2217" data-mp="2ni3vfwgf0v04" />
                          <Button size="sm" variant="flat" color="danger" onClick={() => {
                    const newAddrs = formData.operationalAddresses.filter((_: any, i: number) => i !== idx);
                    setFormData({
                      ...formData,
                      operationalAddresses: newAddrs
                    });
                  }} data-id="element-2218" data-mp="2dkfxyyrfd9e9">
                            <X className="h-4 w-4" data-id="element-2219" data-mp="zj0c48zbyyyi" />
                          </Button>
                        </div>)}
                    <Button size="sm" variant="flat" onClick={() => setFormData({
                  ...formData,
                  operationalAddresses: [...formData.operationalAddresses, '']
                })} data-id="element-2220" data-mp="plvm1y2d6jcf">
                      Aggiungi sede
                    </Button>
                  </div> : <div className="space-y-1" data-id="element-2221" data-mp="2yvcvcax1itfn">
                    {client.operationalAddresses.map((addr: string, idx: number) => <p key={idx} className="font-medium text-slate-900" data-id="element-2222" data-mp="2l61chgy021r1" data-mpi={idx}>
                          {addr}
                        </p>)}
                  </div>}
              </div>

              {/* Legale Rappresentante */}
              <div data-id="element-2223" data-mp="3ht5vx6k6yr9v">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2224" data-mp="3hsd030mrtm6i">
                  Legale Rappresentante
                </label>
                {isEditing ? <Input value={formData.legalRep} onChange={e => setFormData({
                ...formData,
                legalRep: e.target.value
              })} data-id="element-2225" data-mp="38ry1x0opkcll" /> : <p className="font-medium text-slate-900" data-id="element-2226" data-mp="1pwxrtaktomju">
                    {client.legalRep}
                  </p>}
              </div>

              {/* Provenienza */}
              <div data-id="element-2227" data-mp="1ytos5z6eqnk8">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2228" data-mp="1twolpn5nbisl">
                  Provenienza
                </label>
                {isEditing ? <Input value={formData.origin} onChange={e => setFormData({
                ...formData,
                origin: e.target.value
              })} data-id="element-2229" data-mp="164rd04xht6vp" /> : <p className="font-medium text-slate-900" data-id="element-2230" data-mp="ngmb730ebvzj">{client.origin}</p>}
              </div>

              {/* Commercialista */}
              <div data-id="element-2231" data-mp="ih00b081irtw">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2232" data-mp="ifgih56qk8q0">
                  Commercialista
                </label>
                {isEditing ? <Input value={formData.accountant} onChange={e => setFormData({
                ...formData,
                accountant: e.target.value
              })} data-id="element-2233" data-mp="1xt6vb8vu12q" /> : <p className="font-medium text-slate-900" data-id="element-2234" data-mp="gsr2cczbv6o1">
                    {client.accountant}
                  </p>}
              </div>

              {/* Dipendenti */}
              <div data-id="element-2235" data-mp="poymbpawurci">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2236" data-mp="ulp4ci69zx0k">
                  Dipendenti
                </label>
                <div className="flex gap-4 items-center" data-id="element-2237" data-mp="zmcwj768qjwd">
                  <div data-id="element-2238" data-mp="14jwfulhspmv6">
                    <span className="text-xs text-slate-500 mr-2" data-id="element-2239" data-mp="14l7ksomz90nv">Totale:</span>
                    {isEditing ? <input type="number" className="w-16 border rounded p-1" value={formData.employees} onChange={e => setFormData({
                    ...formData,
                    employees: parseInt(e.target.value)
                  })} data-id="element-2240" data-mp="2p3rx2dotddzh" /> : <span className="font-bold text-slate-900" data-id="element-2241" data-mp="32tbs0xc5l6y8">
                        {client.employees}
                      </span>}
                  </div>
                  <div data-id="element-2242" data-mp="2t41yre5kpi8j">
                    <span className="text-xs text-slate-500 mr-2" data-id="element-2243" data-mp="2t2qyhobc6sm6">
                      Censiti:
                    </span>
                    {isEditing ? <input type="number" className="w-16 border rounded p-1" value={formData.employeesCensused} onChange={e => setFormData({
                    ...formData,
                    employeesCensused: parseInt(e.target.value)
                  })} data-id="element-2244" data-mp="3lsg289ffl6rh" /> : <span className="font-bold text-seform-navy" data-id="element-2245" data-mp="wdejz1fv8o3x">
                        {client.employeesCensused}
                      </span>}
                  </div>
                </div>
              </div>

              {/* Newsletter */}
              <div data-id="element-2246" data-mp="1jzbbqru1rb1n">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-2247" data-mp="1k2pbdsbbt65e">
                  Iscrizione Newsletter
                </label>
                {isEditing ? <div className="flex items-center gap-2" data-id="element-2248" data-mp="1t1kj58u0qoat">
                    <span className={formData.newsletter ? 'text-green-600 font-bold' : 'text-slate-400'} data-id="element-2249" data-mp="1o2zhr6315yks">
                      Sì
                    </span>
                    <button onClick={() => setFormData({
                  ...formData,
                  newsletter: !formData.newsletter
                })} className={`w-10 h-5 rounded-full relative transition-colors ${formData.newsletter ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-2250" data-mp="1yo391zvp0lnb">
                      <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${formData.newsletter ? 'left-6' : 'left-1'}`} data-id="element-2251" data-mp="1f0nadp43l8o0" />
                    </button>
                    <span className={!formData.newsletter ? 'text-red-600 font-bold' : 'text-slate-400'} data-id="element-2252" data-mp="160qv2e3llm5m">
                      No
                    </span>
                  </div> : <Badge variant={client.newsletter ? 'success' : 'danger'} data-id="element-2253" data-mp="32wlh2wtqpvas">
                    {client.newsletter ? 'Sì' : 'No'}
                  </Badge>}
              </div>
            </div>
          </Card>

          {/* Azioni Card */}
          <Card title="Azioni" noPadding data-id="element-2254" data-mp="3vjoajajk7fu6">
            {isPending ? <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-id="element-2255" data-mp="3qnz6atwlharj">
                <ClipboardList className="h-10 w-10 mb-3 text-slate-300" data-id="element-2256" data-mp="3mjrgsuibbqs9" />
                <p className="text-sm font-medium" data-id="element-2257" data-mp="3hmsp0im4fvx8">
                  Nessuna azione disponibile
                </p>
                <p className="text-xs mt-1" data-id="element-2258" data-mp="2b6yvo2k120l6">
                  Le azioni saranno disponibili dopo l'attivazione del cliente.
                </p>
              </div> : <div className="max-h-[400px] overflow-y-auto custom-scrollbar" data-id="element-2259" data-mp="lpl4qhr63q7t">
                <ActionsTable actions={clientActions} onActionUpdate={handleActionUpdate} data-id="element-2260" data-mp="qnmybwm7z7xj" />
              </div>}
          </Card>

          {/* Summary Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2261" data-mp="zh8bxcmnej5d">
            {summarySections.map(sec => {
            const isPendingContracts = isPending && sec.id === 'contracts';
            return <Card key={sec.id} title={<div className="flex items-center gap-2" data-id="element-2263" data-mp="1eeiluhjn7gyz">
                      <sec.icon className="h-5 w-5 text-seform-navy" data-id="element-2264" data-mp="1if2fvc8x64gw" />
                      <span className="text-seform-navy" data-id="element-2265" data-mp="1ie9hozo10nad">{sec.title}</span>
                    </div>} actions={isPendingContracts ? undefined : <Button size="sm" variant="ghost" onClick={() => setActiveSection(sec.id)} data-id="element-2266" data-mp="3k8iaslc1i5ho">
                        Vedi tutti
                      </Button>} data-id="element-2262" data-mp="19ct8uwcv0t89">
                  {isPendingContracts ? <div className="flex flex-col items-center justify-center py-8" data-id="element-2267" data-mp="2twdlhsxjtn3x">
                      <FileText className="h-10 w-10 mb-3 text-slate-300" data-id="element-2268" data-mp="264zrlijr1jgu" />
                      <p className="text-sm text-slate-400 mb-4" data-id="element-2269" data-mp="216oe8xu8wqtv">
                        Nessuna offerta o contratto presente.
                      </p>
                      <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-2271" data-mp="2b3t4b8r7p6fy" />} onClick={() => setShowNewOfferModal(true)} data-id="element-2270" data-mp="2fx5foh6vrgqr">
                        Nuova offerta
                      </Button>
                    </div> : <div className="space-y-3" data-id="element-2272" data-mp="3u3io2gwf0vl0">
                      {getSectionData(sec.id).map(row => <div key={row.id} className="flex justify-between items-center text-sm py-1 border-b border-slate-50 last:border-0" data-id="element-2273" data-mp="2ol2t5hksci3">
                          <span className="text-slate-700 truncate max-w-[70%]" data-id="element-2274" data-mp="3p3nto3dflnv7">
                            {row.col1}
                          </span>
                          <Badge size="sm" variant={row.col3 === 'Attivo' || row.col3 === 'Completato' || row.col3 === 'Valido' || row.col3 === 'Pagata' || row.col3 === 'Risolta' || row.col3 === 'Conforme' || row.col3 === 'Approvato' || row.col3 === 'Eseguita' ? 'success' : row.col3 === 'In scadenza' || row.col3 === 'In attesa' || row.col3 === 'In corso' || row.col3 === 'In valutazione' || row.col3 === 'Manutenzione' ? 'warning' : row.col3 === 'Scaduto' || row.col3 === 'Aperta' ? 'danger' : 'neutral'} data-id="element-2275" data-mp="gmt41pln9zz2">
                            {row.col3}
                          </Badge>
                        </div>)}
                    </div>}
                </Card>;
          })}
          </div>
        </div>

        {/* Right Column - Contacts */}
        <div className="space-y-6" data-id="element-2276" data-mp="3ij7yics9yxrr">
          <Card title="Contatti" data-id="element-2277" data-mp="3ihws057g3bwy">
            <div className="space-y-7" data-id="element-2278" data-mp="3nhj6sj2x907v">
              {' '}
              {/* Increased vertical spacing */}
              {/* Telefono Aziendale */}
              <div className="flex items-start gap-3" data-id="element-2279" data-mp="3s9j1nx44x2xl">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2280" data-mp="4767f6uxzpp">
                  <Phone className="h-4 w-4 text-slate-500" data-id="element-2281" data-mp="2zkc533o8qnez" />
                </div>
                <div className="flex-1" data-id="element-2282" data-mp="34idlthy72ana">
                  <p className="text-xs text-slate-500" data-id="element-2283" data-mp="34jge9ub60l5b">Telefono aziendale</p>
                  {isEditing ? <Input value={formData.companyPhone} onChange={e => setFormData({
                  ...formData,
                  companyPhone: e.target.value
                })} className="mt-1" data-id="element-2284" data-mp="38ixjcmmojg9y" /> : <p className="font-medium text-slate-900" data-id="element-2285" data-mp="4hc0kp7woygp">
                      {client.companyPhone}
                    </p>}
                </div>
              </div>
              {/* Email Amministrativa */}
              <div className="flex items-start gap-3" data-id="element-2286" data-mp="dap5kpj8vqj5">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2287" data-mp="8hcgkeanlhpv">
                  <Mail className="h-4 w-4 text-slate-500" data-id="element-2288" data-mp="n5h1zq13c7cc" />
                </div>
                <div className="flex-1" data-id="element-2289" data-mp="i75rre6vvutl">
                  <p className="text-xs text-slate-500" data-id="element-2290" data-mp="iabggsblouos">Email amministrativa</p>
                  {isEditing ? <Input value={formData.adminEmail} onChange={e => setFormData({
                  ...formData,
                  adminEmail: e.target.value
                })} className="mt-1" data-id="element-2291" data-mp="s650ewrnfqgl" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2292" data-mp="2jvxe1q63htkm">
                      {client.adminEmail}
                    </p>}
                </div>
              </div>
              {/* Email Consulenza */}
              <div className="flex items-start gap-3" data-id="element-2293" data-mp="2f21jkxp1iou7">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2294" data-mp="2f0qix6qw5x7b">
                  <Mail className="h-4 w-4 text-slate-500" data-id="element-2295" data-mp="254ngdzs4nwju" />
                </div>
                <div className="flex-1" data-id="element-2296" data-mp="252ki6sgtmx41">
                  <p className="text-xs text-slate-500" data-id="element-2297" data-mp="1r5nq6noyv9lk">Email consulenza</p>
                  {isEditing ? <Input value={formData.consultingEmail} onChange={e => setFormData({
                  ...formData,
                  consultingEmail: e.target.value
                })} className="mt-1" data-id="element-2298" data-mp="1w50cn7bxeenm" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2299" data-mp="1usr3z4tn8q6f">
                      {client.consultingEmail}
                    </p>}
                </div>
              </div>
              {/* PEC & SDI */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-50" data-id="element-2300" data-mp="1ksi33dopigkp">
                <div className="flex items-start gap-3" data-id="element-2301" data-mp="171bw6wj3lgns">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2302" data-mp="1btd7og8fz3cr">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-2303" data-mp="x5idswip6sy3" />
                  </div>
                  <div className="flex-1" data-id="element-2304" data-mp="123af2h58frey">
                    <p className="text-xs text-slate-500" data-id="element-2305" data-mp="1243c2qee32jw">PEC</p>
                    {isEditing ? <Input value={formData.pec} onChange={e => setFormData({
                    ...formData,
                    pec: e.target.value
                  })} className="mt-1" data-id="element-2306" data-mp="o4u76mvmvuv8" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2307" data-mp="ijl7xzyhg3fc">
                        {client.pec}
                      </p>}
                  </div>
                </div>
                <div data-id="element-2308" data-mp="semv4q976vfa">
                  <p className="text-xs text-slate-500" data-id="element-2309" data-mp="sdv1hkz5v7rn">SDI</p>
                  {isEditing ? <Input value={formData.sdi} onChange={e => setFormData({
                  ...formData,
                  sdi: e.target.value
                })} className="mt-1" data-id="element-2310" data-mp="nfac6glg68hz" /> : <p className="font-medium text-slate-900" data-id="element-2311" data-mp="169774neuuinc">{client.sdi}</p>}
                </div>
              </div>
              {/* Referenti Section */}
              <div className="pt-4 border-t border-slate-100" data-id="element-2312" data-mp="8ucis78z006f">
                <p className="text-xs font-bold text-seform-navy uppercase mb-3" data-id="element-2313" data-mp="cuwh3e5a59u5">
                  Referenti
                </p>

                <div className="space-y-6" data-id="element-2314" data-mp="hluvg86pjxur">
                  {/* Persona di riferimento & Cellulare - Side by side */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-2315" data-mp="mjwif4798hbi">
                    <div data-id="element-2316" data-mp="rj9dudc6ywpp">
                      <p className="text-xs text-slate-500" data-id="element-2317" data-mp="riprmkfidm5v">
                        Persona di riferimento
                      </p>
                      {isEditing ? <Input value={formData.contactPerson} onChange={e => setFormData({
                      ...formData,
                      contactPerson: e.target.value
                    })} className="mt-1" data-id="element-2318" data-mp="10kz59j62pvnh" /> : <div className="flex items-center gap-2" data-id="element-2319" data-mp="2q0t2aakctwgl">
                          <User className="h-3 w-3 text-slate-400" data-id="element-2320" data-mp="2l1zyxays7lu5" />
                          <span className="font-medium text-slate-900" data-id="element-2321" data-mp="2l059bjckf5jq">
                            {client.contactPerson}
                          </span>
                        </div>}
                    </div>

                    <div data-id="element-2322" data-mp="38rkerkzn22z7">
                      <p className="text-xs text-slate-500" data-id="element-2323" data-mp="38tdoxa9gn5by">
                        Cellulare referente
                      </p>
                      {isEditing ? <Input value={formData.contactMobile} onChange={e => setFormData({
                      ...formData,
                      contactMobile: e.target.value
                    })} className="mt-1" data-id="element-2324" data-mp="2zrxhnbdxzjby" /> : <div className="flex items-center gap-2" data-id="element-2325" data-mp="3h1849tgjtzut">
                          <Smartphone className="h-3 w-3 text-slate-400" data-id="element-2326" data-mp="3391nc55knoub" />
                          <span className="font-medium text-slate-900" data-id="element-2327" data-mp="379tzp79qq9ut">
                            {client.contactMobile}
                          </span>
                        </div>}
                    </div>
                  </div>

                  {/* Referente sicurezza & Cellulare - Side by side */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-2328" data-mp="3lzjc5ynaa0ae">
                    <div data-id="element-2329" data-mp="3m12xi3nsr1j1">
                      <p className="text-xs text-slate-500" data-id="element-2330" data-mp="3qxbe7lhn72sb">
                        Referente sicurezza
                      </p>
                      {isEditing ? <Input value={formData.safetyReferent} onChange={e => setFormData({
                      ...formData,
                      safetyReferent: e.target.value
                    })} className="mt-1" data-id="element-2331" data-mp="2alxoyj0zi8ri" /> : <div className="flex items-center gap-2" data-id="element-2332" data-mp="2c0gvnw60xlqw">
                          <Shield className="h-3 w-3 text-slate-400" data-id="element-2333" data-mp="1ob5re4pgt980" />
                          <span className="font-medium text-slate-900" data-id="element-2334" data-mp="1obfki15t3whx">
                            {client.safetyReferent}
                          </span>
                        </div>}
                    </div>

                    <div data-id="element-2335" data-mp="1ta8vnmxmfbex">
                      <p className="text-xs text-slate-500" data-id="element-2336" data-mp="1t9z43uwxgw74">
                        Cellulare Ref. Sicurezza
                      </p>
                      {isEditing ? <Input value={formData.safetyMobile} onChange={e => setFormData({
                      ...formData,
                      safetyMobile: e.target.value
                    })} className="mt-1" data-id="element-2337" data-mp="10luy18ag2h3w" /> : <div className="flex items-center gap-2" data-id="element-2338" data-mp="3ee6d9g47urci">
                          <Smartphone className="h-3 w-3 text-slate-400" data-id="element-2339" data-mp="2hp8fdp166db4" />
                          <span className="font-medium text-slate-900" data-id="element-2340" data-mp="2hr1mf5fp2zjn">
                            {client.safetyMobile || '-'}
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
              {/* Other Emails */}
              <div className="pt-4 border-t border-slate-100 space-y-5" data-id="element-2341" data-mp="5zxraqw78ytb">
                <div className="flex items-start gap-3" data-id="element-2342" data-mp="ayisasj9zmit">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2343" data-mp="awxw6yhbbviu">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-2344" data-mp="l4k1573f8eid" />
                  </div>
                  <div className="flex-1" data-id="element-2345" data-mp="g3wbopuuc5q9">
                    <p className="text-xs text-slate-500" data-id="element-2346" data-mp="bce9xzkzu148">Email formazione</p>
                    {isEditing ? <Input value={formData.trainingEmail} onChange={e => setFormData({
                    ...formData,
                    trainingEmail: e.target.value
                  })} className="mt-1" data-id="element-2347" data-mp="7bkjozk2wa86" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2348" data-mp="3kimdiliv1r7o">
                        {client.trainingEmail}
                      </p>}
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2349" data-mp="1irj05ohl5yjf">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2350" data-mp="21z7t5poz4r5a">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-2351" data-mp="260t06uetub7k" />
                  </div>
                  <div className="flex-1" data-id="element-2352" data-mp="1s9ekawg7uq4q">
                    <p className="text-xs text-slate-500" data-id="element-2353" data-mp="1s8mvq5l1dsny">Email informative</p>
                    {isEditing ? <Input value={formData.infoEmail} onChange={e => setFormData({
                    ...formData,
                    infoEmail: e.target.value
                  })} className="mt-1" data-id="element-2354" data-mp="1x64w37kzmzkc" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2355" data-mp="1ebhjtoyn5zks">
                        {client.infoEmail}
                      </p>}
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2356" data-mp="38l1ydj66nl9n">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2357" data-mp="3is4mkeednlwq">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-2358" data-mp="3du333sd1hkkq" />
                  </div>
                  <div className="flex-1" data-id="element-2359" data-mp="3rl7uk76976jz">
                    <p className="text-xs text-slate-500" data-id="element-2360" data-mp="3msyav8vqf2q5">Email newsletter</p>
                    {isEditing ? <Input value={formData.newsletterEmail} onChange={e => setFormData({
                    ...formData,
                    newsletterEmail: e.target.value
                  })} className="mt-1" data-id="element-2361" data-mp="5bx674slm6sq" /> : <p className="font-medium text-slate-900 break-all" data-id="element-2362" data-mp="2g3q1c1matpbp">
                        {client.newsletterEmail}
                      </p>}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Ticket History Modal */}
      <Modal isOpen={showTicketHistory} onClose={() => setShowTicketHistory(false)} title="Storico Ticket" size="lg" data-id="element-2363" data-mp="1g2igabie1lfv">
        <div className="space-y-4" data-id="element-2364" data-mp="16cqlphaagsbe">
          {[1, 2, 3].map(i => <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-2365" data-mp="16acnx0o85205">
              <div className="flex justify-between items-start mb-2" data-id="element-2366" data-mp="xb9763cj4yot">
                <span className="font-mono text-xs text-slate-500" data-id="element-2367" data-mp="scfsw8pf53nl">
                  TKT-2024-{100 + i}
                </span>
                <Badge variant={i === 1 ? 'warning' : 'success'} data-id="element-2368" data-mp="ne4hvune0n3s">
                  {i === 1 ? 'In corso' : 'Chiuso'}
                </Badge>
              </div>
              <h4 className="font-medium text-slate-900 mb-1" data-id="element-2369" data-mp="2mo2xuo20ykhp">
                Richiesta assistenza tecnica {i}
              </h4>
              <p className="text-sm text-slate-600 mb-2" data-id="element-2370" data-mp="2q8wdlujy2ckd">
                Descrizione breve del problema riscontrato dal cliente...
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-2371" data-mp="2u856yyb2o8fe">
                <span className="flex items-center gap-1" data-id="element-2372" data-mp="2z1zk1iw69p5i">
                  <Clock className="h-3 w-3" data-id="element-2373" data-mp="33zhtrbi0k8ek" /> 12/05/2024
                </span>
                <span className="flex items-center gap-1" data-id="element-2374" data-mp="341m1y5my3tkn">
                  <User className="h-3 w-3" data-id="element-2375" data-mp="27b37ykhxroja" /> Mario Rossi
                </span>
              </div>
            </div>)}
        </div>
        <div className="mt-6 flex justify-end" data-id="element-2376" data-mp="2ga6r1oh3s3xs">
          <Button onClick={() => setShowTicketHistory(false)} data-id="element-2377" data-mp="2g9f8be8lu0ox">Chiudi</Button>
        </div>
      </Modal>

      {/* Nuova Offerta Modal (for pending clients) */}
      <Modal isOpen={showNewOfferModal} onClose={() => {
      setShowNewOfferModal(false);
      setOfferSaveSuccess(false);
      resetOfferMetadata();
    }} title="Nuova offerta" size="2xl" data-id="element-2378" data-mp="3rplyxnauog7o">
        <AnimatePresence mode="wait" data-id="element-2379" data-mp="36yq5bvgw6tcz">
          {offerSaveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2380" data-mp="321zqfbgtq3t5">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2381" data-mp="2oa33dlu0ik3m">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2382" data-mp="20kh30v1wlyu1" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2383" data-mp="g2gdr14kr03b">
                Offerta creata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2384" data-mp="kuzqxwzcnt7l">
              <div className="space-y-8" data-id="element-2385" data-mp="b10sp92ectkx">
                <section data-id="element-2386" data-mp="ys69bqg5vpwa">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2387" data-mp="ytze1077s01n">
                    <FileText className="h-4 w-4" data-id="element-2388" data-mp="13poinhy3c74t" /> Dati generali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2389" data-mp="prghsyxxolod">
                    <ProgressiveNumberInput type="contract" value="" onChange={() => {}} disabled={false} data-id="element-2390" data-mp="tsjuxdolk3lw" />
                    <SearchableSelect label="Cliente" value={client?.name || ''} onChange={() => {}} options={[{
                  value: client?.name || '',
                  label: client?.name || ''
                }]} required data-id="element-2391" data-mp="3eldk75tczhdp" />
                    <SearchableSelect label="Prodotto" value={offerProduct} onChange={e => handleOfferProductSelection(e.target.value)} options={offerProductOptions} placeholder="Cerca e seleziona un prodotto..." required data-id="element-2392" data-mp="hkez2nw3b09x" />
                    <Select label="Tipologia" value={offerType} onChange={e => setOfferType(e.target.value)} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, ...offerProductTypes.map(type => ({
                  value: type,
                  label: type
                }))]} data-id="element-2393" data-mp="2sczq6wnauijp" />
                    <Input label="Codice prodotto" value={offerProductCode} onChange={e => setOfferProductCode(e.target.value)} placeholder="Inserisci il codice prodotto" data-id="element-2394" data-mp="1hm3cdsgfsmj2" />
                    <Select label="Ambito" value={offerScope} onChange={e => setOfferScope(e.target.value)} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, ...offerScopes.map(scope => ({
                  value: scope,
                  label: scope
                }))]} data-id="element-2395" data-mp="1vkt6cz7ym637" />
                    <Select label="Responsabile sviluppo" options={[{
                  value: 'alessio',
                  label: 'Alessio'
                }, {
                  value: 'gianfranco',
                  label: 'Gian Franco'
                }, {
                  value: 'lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-2396" data-mp="1igxmnjpbzh9e" />
                    <Select label="Commissionato da:" options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Commerciale SEF',
                  label: 'Commerciale SEF'
                }, {
                  value: 'Esterni e partner',
                  label: 'Esterni e partner'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-2397" data-mp="2ps20s6ysyaql" />
                    <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" data-id="element-2398" data-mp="2238zrw3bpcp4" />
                    <Input label="Data dell'offerta" type="date" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-2399" data-mp="1s49y4xgz6hbm" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-2400" data-mp="23l8yzgrl4ira"></div>

                <section data-id="element-2401" data-mp="1yl3dyjftlui6">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2402" data-mp="1ykbir9q4xysf">
                    <Euro className="h-4 w-4" data-id="element-2403" data-mp="1tm9xci58lnds" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2404" data-mp="1pmhrid789vc4">
                    <Input label="Importo (€)" type="number" placeholder="0.00" required data-id="element-2405" data-mp="1kt6q4rl6q6rl" />
                    <Input label="Durata (mesi)" type="number" placeholder="12" data-id="element-2406" data-mp="1ax3d2qosonpv" />
                    <Select label="Rinnovo tacito" options={[{
                  value: 'si',
                  label: 'Sì'
                }, {
                  value: 'no',
                  label: 'No'
                }]} data-id="element-2407" data-mp="11vm2toy2jg93" />
                    <Input label="Scadenza" type="date" data-id="element-2408" data-mp="3riqkean8j8dq" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2409" data-mp="2tvhve5o0zh6z">
                  <Button variant="flat" onClick={() => {
                setShowNewOfferModal(false);
                resetOfferMetadata();
              }} data-id="element-2410" data-mp="2yt9sde3j1ti1">
                    Annulla
                  </Button>
                  <Button onClick={() => {
                setOfferSaveSuccess(true);
                setTimeout(() => {
                  setOfferSaveSuccess(false);
                  setShowNewOfferModal(false);
                  resetOfferMetadata();
                }, 1500);
              }} data-id="element-2411" data-mp="2b4icnm74j3m5">
                    Crea offerta
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}